import { createContext, useContext, useState, useCallback } from 'react';

const ConfigContext = createContext(null);

const initialConfig = {
  // 智能体基本信息
  agentInfo: {
    name: '',
    tags: [],
    description: '',
    avatar: null,
  },
  // API 配置
  apiConfig: {
    platform: 'zhijia',
    apiName: '',
    apiUrl: '',
    apiDescription: '',
    authType: 'API_KEY',
    apiKey: '',
    bearerToken: '',
    method: 'POST',
    headers: [{ key: 'Content-Type', value: 'application/json' }],
    body: `{
  "query": "{{USER_INPUT}}"
}`,
    isStreaming: false,
    // 会话截断策略
    truncationStrategy: {
      type: 'token',
      tokenLimit: 8192,
      roundsLimit: 15,
    },
  },
  // 提示词模板配置
  promptTemplateMode: 'local', // 'local' | 'api'
  // 后台配置模式
  localTemplates: [
    {
      id: 1,
      title: '故障分析（级联示例）',
      template: '分析 {{deviceType}} 的 {{faultType}} 故障，给出处理建议',
      variables: [
        { name: 'deviceType', uiType: 'select', logicType: 'independent', optionSource: 'manual', dependsOn: null, options: ['路由器', '交换机'], cascadingMap: {} },
        { name: 'faultType', uiType: 'select-cascade', logicType: 'cascading', optionSource: 'manual', dependsOn: 'deviceType', options: [], cascadingMap: { '路由器': ['路由震荡', '接口Down', 'CPU过高'], '交换机': ['端口环路', 'VLAN配置错误', 'STP故障'] } },
      ],
      previewValues: {},
    }
  ],
  currentLocalTemplateIndex: 0,
  // 接口拉取模式
  apiConfigMode: {
    url: '',
    method: 'GET',
    auth: {
      type: 'inherit', // 'inherit' | 'custom' | 'none'
      apiKey: '',
      bearerToken: '',
    },
    params: [],
    cacheDuration: 300,
    fieldMapping: {
      listPath: '',
      titleField: '',
      templateField: '',
      variablesField: '',
      variable: {
        nameField: '',
        typeField: '',
        parentField: '',
        staticOptionsField: '',
        dynamicOptionsApiField: '',
      }
    },
    fallback: {
      strategy: 'hide', // 'fallback' | 'hide'
      timeout: 5000,
    }
  },
  // 入参配置
  inputParams: {
    businessParams: [],
  },
  // 配置状态
  configStatus: 'incomplete', // 'incomplete' | 'complete'
};

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(initialConfig);
  const [isLoading, setIsLoading] = useState(false);

  const updateAgentInfo = useCallback((updates) => {
    setConfig(prev => ({
      ...prev,
      agentInfo: { ...prev.agentInfo, ...updates }
    }));
  }, []);

  const addTag = useCallback((tag) => {
    setConfig(prev => ({
      ...prev,
      agentInfo: {
        ...prev.agentInfo,
        tags: [...prev.agentInfo.tags, tag]
      }
    }));
  }, []);

  const removeTag = useCallback((index) => {
    setConfig(prev => ({
      ...prev,
      agentInfo: {
        ...prev.agentInfo,
        tags: prev.agentInfo.tags.filter((_, i) => i !== index)
      }
    }));
  }, []);

  const updateApiConfig = useCallback((updates) => {
    setConfig(prev => ({
      ...prev,
      apiConfig: { ...prev.apiConfig, ...updates }
    }));
  }, []);

  const addHeader = useCallback(() => {
    setConfig(prev => ({
      ...prev,
      apiConfig: {
        ...prev.apiConfig,
        headers: [...prev.apiConfig.headers, { key: '', value: '' }]
      }
    }));
  }, []);

  const updateHeader = useCallback((index, field, value) => {
    setConfig(prev => ({
      ...prev,
      apiConfig: {
        ...prev.apiConfig,
        headers: prev.apiConfig.headers.map((h, i) => 
          i === index ? { ...h, [field]: value } : h
        )
      }
    }));
  }, []);

  const removeHeader = useCallback((index) => {
    setConfig(prev => ({
      ...prev,
      apiConfig: {
        ...prev.apiConfig,
        headers: prev.apiConfig.headers.filter((_, i) => i !== index)
      }
    }));
  }, []);

  const updateInputParams = useCallback((updates) => {
    setConfig(prev => ({
      ...prev,
      inputParams: { ...prev.inputParams, ...updates }
    }));
  }, []);

  const addBusinessParam = useCallback((param) => {
    setConfig(prev => ({
      ...prev,
      inputParams: {
        ...prev.inputParams,
        businessParams: [...prev.inputParams.businessParams, param]
      }
    }));
  }, []);

  const updateBusinessParam = useCallback((index, updates) => {
    setConfig(prev => ({
      ...prev,
      inputParams: {
        ...prev.inputParams,
        businessParams: prev.inputParams.businessParams.map((p, i) =>
          i === index ? { ...p, ...updates } : p
        )
      }
    }));
  }, []);

  const removeBusinessParam = useCallback((index) => {
    setConfig(prev => ({
      ...prev,
      inputParams: {
        ...prev.inputParams,
        businessParams: prev.inputParams.businessParams.filter((_, i) => i !== index)
      }
    }));
  }, []);

  // 模式切换
  const setPromptTemplateMode = useCallback((mode) => {
    setConfig(prev => ({ ...prev, promptTemplateMode: mode }));
  }, []);

  // 本地模板方法
  const updateLocalPromptTemplate = useCallback((updates) => {
    setConfig(prev => {
      const newTemplates = [...prev.localTemplates];
      newTemplates[prev.currentLocalTemplateIndex] = {
        ...newTemplates[prev.currentLocalTemplateIndex],
        ...updates
      };
      return {
        ...prev,
        localTemplates: newTemplates
      };
    });
  }, []);

  const updateLocalPromptVariable = useCallback((index, updates) => {
    setConfig(prev => {
      const newTemplates = [...prev.localTemplates];
      const currentTemplate = { ...newTemplates[prev.currentLocalTemplateIndex] };
      
      const updatedVariables = currentTemplate.variables.map((v, i) =>
        i === index ? { ...v, ...updates } : v
      );
      
      const affectedVars = updatedVariables
        .map((v, idx) => ({ v, idx }))
        .filter(({ v }) => v.dependsOn === updatedVariables[index]?.name);
      
      let newPreviewValues = { ...currentTemplate.previewValues };
      affectedVars.forEach(({ idx, v }) => {
        if (newPreviewValues[v.name]) {
          delete newPreviewValues[v.name];
        }
      });

      currentTemplate.variables = updatedVariables;
      currentTemplate.previewValues = newPreviewValues;
      newTemplates[prev.currentLocalTemplateIndex] = currentTemplate;
      
      return { ...prev, localTemplates: newTemplates };
    });
  }, []);

  const addLocalPromptVariable = useCallback((variable) => {
    setConfig(prev => {
      const newTemplates = [...prev.localTemplates];
      const currentTemplate = { ...newTemplates[prev.currentLocalTemplateIndex] };
      currentTemplate.variables = [...currentTemplate.variables, variable];
      newTemplates[prev.currentLocalTemplateIndex] = currentTemplate;
      return { ...prev, localTemplates: newTemplates };
    });
  }, []);

  const removeLocalPromptVariable = useCallback((index) => {
    setConfig(prev => {
      const newTemplates = [...prev.localTemplates];
      const currentTemplate = { ...newTemplates[prev.currentLocalTemplateIndex] };
      
      const removedVar = currentTemplate.variables[index];
      const affectedVars = currentTemplate.variables
        .filter((v, i) => i !== index && v.dependsOn === removedVar.name);
      
      let newPreviewValues = { ...currentTemplate.previewValues };
      delete newPreviewValues[removedVar.name];
      affectedVars.forEach(v => {
        delete newPreviewValues[v.name];
      });

      currentTemplate.variables = currentTemplate.variables.filter((_, i) => i !== index);
      currentTemplate.previewValues = newPreviewValues;
      newTemplates[prev.currentLocalTemplateIndex] = currentTemplate;
      
      return { ...prev, localTemplates: newTemplates };
    });
  }, []);

  const updateLocalPreviewValue = useCallback((variableName, value) => {
    setConfig(prev => {
      const newTemplates = [...prev.localTemplates];
      const currentTemplate = { ...newTemplates[prev.currentLocalTemplateIndex] };
      
      const variable = currentTemplate.variables.find(v => v.name === variableName);
      let newPreviewValues = { ...currentTemplate.previewValues, [variableName]: value };

      if (variable?.dependsOn) {
        const parentValue = newPreviewValues[variable.dependsOn];
        if (!parentValue) {
          delete newPreviewValues[variableName];
        }
      }

      if (value) {
        const affectedVars = currentTemplate.variables.filter(
          v => v.dependsOn === variableName
        );
        affectedVars.forEach(v => {
          delete newPreviewValues[v.name];
        });
      }

      currentTemplate.previewValues = newPreviewValues;
      newTemplates[prev.currentLocalTemplateIndex] = currentTemplate;
      
      return { ...prev, localTemplates: newTemplates };
    });
  }, []);

  const addLocalTemplate = useCallback(() => {
    setConfig(prev => ({
      ...prev,
      localTemplates: [...prev.localTemplates, {
        id: Date.now(),
        title: '新模板',
        template: '',
        variables: [],
        previewValues: {},
      }],
      currentLocalTemplateIndex: prev.localTemplates.length
    }));
  }, []);

  const removeLocalTemplate = useCallback((index) => {
    setConfig(prev => {
      const newTemplates = prev.localTemplates.filter((_, i) => i !== index);
      let newCurrentIndex = prev.currentLocalTemplateIndex;
      if (newCurrentIndex >= newTemplates.length) {
        newCurrentIndex = Math.max(0, newTemplates.length - 1);
      }
      return {
        ...prev,
        localTemplates: newTemplates,
        currentLocalTemplateIndex: newCurrentIndex
      };
    });
  }, []);

  const switchLocalTemplate = useCallback((index) => {
    setConfig(prev => ({
      ...prev,
      currentLocalTemplateIndex: index
    }));
  }, []);

  const updateLocalTemplateTitle = useCallback((index, title) => {
    setConfig(prev => {
      const newTemplates = [...prev.localTemplates];
      newTemplates[index] = { ...newTemplates[index], title };
      return { ...prev, localTemplates: newTemplates };
    });
  }, []);

  // API 配置方法
  const updateApiConfigMode = useCallback((updates) => {
    setConfig(prev => ({
      ...prev,
      apiConfigMode: { ...prev.apiConfigMode, ...updates }
    }));
  }, []);

  const updateApiConfigAuth = useCallback((updates) => {
    setConfig(prev => ({
      ...prev,
      apiConfigMode: {
        ...prev.apiConfigMode,
        auth: { ...prev.apiConfigMode.auth, ...updates }
      }
    }));
  }, []);

  const updateApiConfigFieldMapping = useCallback((path, value) => {
    setConfig(prev => {
      const newFieldMapping = { ...prev.apiConfigMode.fieldMapping };
      const keys = path.split('.');
      let current = newFieldMapping;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return {
        ...prev,
        apiConfigMode: { ...prev.apiConfigMode, fieldMapping: newFieldMapping }
      };
    });
  }, []);

  const addApiConfigParam = useCallback(() => {
    setConfig(prev => ({
      ...prev,
      apiConfigMode: {
        ...prev.apiConfigMode,
        params: [...prev.apiConfigMode.params, { key: '', value: '' }]
      }
    }));
  }, []);

  const updateApiConfigParam = useCallback((index, updates) => {
    setConfig(prev => {
      const newParams = [...prev.apiConfigMode.params];
      newParams[index] = { ...newParams[index], ...updates };
      return {
        ...prev,
        apiConfigMode: { ...prev.apiConfigMode, params: newParams }
      };
    });
  }, []);

  const removeApiConfigParam = useCallback((index) => {
    setConfig(prev => ({
      ...prev,
      apiConfigMode: {
        ...prev.apiConfigMode,
        params: prev.apiConfigMode.params.filter((_, i) => i !== index)
      }
    }));
  }, []);

  const value = {
    config,
    isLoading,
    setIsLoading,
    updateAgentInfo,
    addTag,
    removeTag,
    updateApiConfig,
    addHeader,
    updateHeader,
    removeHeader,
    updateInputParams,
    addBusinessParam,
    updateBusinessParam,
    removeBusinessParam,
    // 新的提示词模板方法
    setPromptTemplateMode,
    // 本地模板方法
    updateLocalPromptTemplate,
    updateLocalPromptVariable,
    addLocalPromptVariable,
    removeLocalPromptVariable,
    updateLocalPreviewValue,
    addLocalTemplate,
    removeLocalTemplate,
    switchLocalTemplate,
    updateLocalTemplateTitle,
    // API 配置方法
    updateApiConfigMode,
    updateApiConfigAuth,
    updateApiConfigFieldMapping,
    addApiConfigParam,
    updateApiConfigParam,
    removeApiConfigParam,
    setConfigStatus: (status) => setConfig(prev => ({ ...prev, configStatus: status })),
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}
